import { FieldArray } from 'react-final-form-arrays';

import { Divider, Typography, Box, Stack, tokens } from '@cast/design-system';

import { PreparedWorkload } from 'features/workload-optimization';

import { AutoInstrumentField } from '../../../../../_woop-settings/_components/fields/AutoInstrumentField';
import { JvmMemoryField } from '../../../../../_woop-settings/_components/fields/JvmMemoryField';
import { vpaSettingsConstants } from '../../../../../_woop-settings/_constants/settingsConstants';
import { VerticalRightsizingPolicyFormModel } from '../../../_types';
import { OverrideGroupOverrideButton } from '../../override/OverrideGroupOverrideButton';
import { OverrideGroupProvider } from '../../override/OverrideGroupProvider';

import { useControlsGroupState } from '@cast/shared-rff-ui';

const { jvmHeapScaling, autoInstrumentation } =
  vpaSettingsConstants.customMetrics;

const jvmMemoryTooltip = (
  <Stack gap={4}>
    <Typography variant="P12R">{jvmHeapScaling.description}</Typography>
    <Typography
      variant="P12R"
      sx={{ color: tokens.theme.fg.structural.secondary }}
    >
      {jvmHeapScaling.alert}
    </Typography>
  </Stack>
);

const autoInstrumentTooltip = (
  <Stack gap={4}>
    <Typography variant="P12R">{autoInstrumentation.description}</Typography>
    <Typography
      variant="P12R"
      sx={{ color: tokens.theme.fg.structural.secondary }}
    >
      {autoInstrumentation.alert}
    </Typography>
  </Stack>
);

export const CustomMetricsSettingsArrayField = ({
  workload,
}: {
  workload: PreparedWorkload;
}) => {
  const { name = '' } = useControlsGroupState({
    name: 'customMetrics',
  });

  return (
    <Stack>
      <FieldArray name={name}>
        {({ fields }) =>
          fields.map((name, index) => (
            <Box key={name} data-testid={`container-${index}`}>
              <OverrideGroupProvider
                name={`customMetrics[${index}]`}
                canOverride
              >
                <Stack gap={24}>
                  <Typography variant="h6" data-testid="container-name">
                    {workload.containers[index].name}
                  </Typography>
                  <Stack direction="row" gap={16}>
                    <Box sx={{ flex: 1 }}>
                      <JvmMemoryField
                        name="jvmMemoryOptimization"
                        tooltipText={jvmMemoryTooltip}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <AutoInstrumentField
                        name="jvmAutoInstrument"
                        tooltipText={autoInstrumentTooltip}
                      />
                    </Box>
                  </Stack>
                  <OverrideGroupOverrideButton<
                    VerticalRightsizingPolicyFormModel['customMetrics'][number]
                  >
                    onRevert={(workload) => ({
                      containerName: workload.containers[index].name,
                      jvmMemoryOptimization:
                        !!workload.verticalScalingPolicy?.recommendationPolicies
                          .jvm?.memory?.optimization,
                      jvmAutoInstrument:
                        !!workload.verticalScalingPolicy?.recommendationPolicies
                          .jvm?.autoInstrument,
                    })}
                  />
                </Stack>
              </OverrideGroupProvider>

              {fields.length && index < fields.length - 1 && (
                <Divider
                  color={tokens.theme.border.structural.neutralSubtle}
                  sx={{ mt: 12, mb: 24 }}
                />
              )}
            </Box>
          ))
        }
      </FieldArray>
    </Stack>
  );
};
